/**
 *  @class
 *  @function CartDiscounts
 */
if (!customElements.get('cart-discounts')) {
  let discountUpdatePending = false;

  class CartDiscounts extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      this.discounts();
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      }, {
        id: 'Cart-Drawer',
        section: document.getElementById('Cart-Drawer')?.dataset.sectionId || 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
    }
    discounts() {
      const button = this.querySelector('.cart-discounts--button');
      button?.addEventListener('click', event => {
        event.preventDefault();
        this.updateDiscount();
      });
      this.querySelector('.cart-discounts--input')?.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.updateDiscount();
      });
      this.querySelectorAll('.cart-discounts--remove').forEach(removeButton => {
        removeButton.addEventListener('click', this.removeDiscount.bind(this));
      });
    }
    updateDiscount() {
      const discountCode = this.querySelector('.cart-discounts--input');
      const discountCodeValue = discountCode?.value.trim();
      if (!discountCodeValue || this.pending) return;
      const existingDiscounts = this.existingDiscounts();

      if (existingDiscounts.some(code => code.toLowerCase() === discountCodeValue.toLowerCase())) return;
      existingDiscounts.push(discountCodeValue);

      return this.renderDiscounts(existingDiscounts, discountCodeValue);
    }
    removeDiscount(event) {
      event.preventDefault();
      const discountCode = event.currentTarget.closest('.cart-discounts--row')?.querySelector('.cart-discounts--name')?.textContent.trim();
      if (!discountCode || this.pending) return;
      const existingDiscounts = this.existingDiscounts();
      const index = existingDiscounts.indexOf(discountCode);
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      return this.renderDiscounts(existingDiscounts);
    }
    existingDiscounts() {
      return [...this.querySelectorAll('.cart-discounts--name')].map(pill => pill.textContent.trim());
    }
    showError(code, message) {
      const details = this.closest('details');
      if (details) details.open = true;
      this.querySelector('.cart-discounts--error')?.removeAttribute('hidden');
      const errorText = this.querySelector('.cart-discounts--error-text');
      if (message && errorText) errorText.textContent = message;
      const input = this.querySelector('.cart-discounts--input');
      if (input && code) input.value = code;
      input?.focus({ preventScroll: true });
    }
    async renderDiscounts(existingDiscounts, discountCodeValue) {
      if (this.pending || discountUpdatePending) return false;
      this.pending = true;
      discountUpdatePending = true;
      const sourceRoot = this.closest('#Cart, #Cart-Drawer');
      const checkoutButtons = new Set();
      const expressCheckoutGroups = new Set();
      const lockCheckout = () => {
        document.querySelectorAll('#Cart .checkout-button, #Cart-Drawer .checkout-button').forEach(checkoutButton => {
          if (checkoutButton.disabled) return;
          checkoutButtons.add(checkoutButton);
          checkoutButton.disabled = true;
          checkoutButton.setAttribute('aria-busy', 'true');
        });
        document.querySelectorAll('#Cart .cart__dynamic-checkout-buttons, #Cart-Drawer .cart__dynamic-checkout-buttons').forEach(expressCheckoutGroup => {
          if (expressCheckoutGroup.inert) return;
          expressCheckoutGroups.add(expressCheckoutGroup);
          expressCheckoutGroup.inert = true;
          expressCheckoutGroup.setAttribute('aria-busy', 'true');
        });
      };
      lockCheckout();
      // Concurrent cart responses can replace the controls before this request completes.
      const checkoutObserver = typeof MutationObserver !== 'undefined' && document.body
        ? new MutationObserver(lockCheckout)
        : null;
      checkoutObserver?.observe(document.body, { childList: true, subtree: true });
      const button = this.querySelector('.cart-discounts--button');
      button?.classList.add('loading');
      if (button) button.disabled = true;
      this.querySelector('.cart-discounts--error')?.setAttribute('hidden', '');
      const sections = this.getSectionsToRender();
      const body = JSON.stringify({
        discount: existingDiscounts.join(','),
        sections: sections.map(section => section.section),
        sections_url: window.location.pathname
      });
      try {
        const response = await fetch(`${theme.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body
        });
        if (!response.ok) throw new Error('Discount update failed');
        const parsedState = JSON.parse(await response.text());
        for (const section of sections) {
          const html = parsedState.sections?.[section.section];
          if (!html) continue;
          const target = document.getElementById(section.id);
          const elementToReplace = target?.querySelector(section.selector) || target;
          const content = this.getSectionInnerHTML(html, section.selector);
          if (elementToReplace && content != null) elementToReplace.innerHTML = content;
        }
        const accepted = parsedState.discount_codes?.some(discount =>
          discount.code.toLowerCase() === discountCodeValue?.toLowerCase() && discount.applicable !== false
        );
        const rejected = discountCodeValue && !accepted;
        if (rejected) {
          (sourceRoot?.querySelector('cart-discounts') || this).showError(discountCodeValue);
        }
        return !rejected;
      } catch (error) {
        const current = sourceRoot?.querySelector('cart-discounts') || this;
        current.showError(discountCodeValue, theme.strings.cartError);
        return false;
      } finally {
        checkoutObserver?.disconnect();
        const current = sourceRoot?.querySelector('cart-discounts') || this;
        const currentButton = current.querySelector('.cart-discounts--button') || button;
        currentButton?.classList.remove('loading');
        if (currentButton) currentButton.disabled = false;
        checkoutButtons.forEach(checkoutButton => {
          if (!checkoutButton.isConnected) return;
          checkoutButton.disabled = false;
          checkoutButton.removeAttribute('aria-busy');
        });
        expressCheckoutGroups.forEach(expressCheckoutGroup => {
          if (!expressCheckoutGroup.isConnected) return;
          expressCheckoutGroup.inert = false;
          expressCheckoutGroup.removeAttribute('aria-busy');
        });
        this.pending = false;
        discountUpdatePending = false;
      }
    }
  }
  customElements.define('cart-discounts', CartDiscounts);
}
